import { getItem, getOffer, RARITY_COLORS } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { closeButton } from './hud.js';
import { paintItemIcon } from './item-icon.js';
import { uiFormat, uiText } from './text.js';
import './shop-modals.css';

/**
 * The thank-you window (ADR 0031), once the server has put a purchase in the
 * bag: the pieces bought, where they went, and a way straight to them. It
 * shows only what the server delivered: it opens on a `delivered` order.
 */

export interface ThanksModalOptions {
  offerId: string;
  characterName: string;
  icons: CssAtlas | null;
  /** Open the inventory on its look side, where the pieces wait to be worn. */
  onShowInventory: () => void;
}

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

export class ThanksModal {
  private readonly root: HTMLElement;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.close();
  };

  constructor(options: ThanksModalOptions) {
    const offer = getOffer(options.offerId);
    this.root = document.createElement('div');
    this.root.className = 'shop-modal shop-modal--thanks';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', uiText('Merci pour ton achat'));

    const frame = document.createElement('section');
    frame.className = 'shop-modal__window';
    const thanks = document.createElement('p');
    thanks.className = 'shop-modal__thanks';
    thanks.textContent = uiText('Merci !');
    const lead = document.createElement('p');
    lead.className = 'shop-modal__lead';
    const name = document.createElement('strong');
    name.style.color = '#ffc82e';
    name.textContent = uiText(offer?.name ?? options.offerId);
    lead.append(
      uiText('Ton achat '),
      name,
      uiFormat(' est arrivé dans le sac de {name}.', { name: options.characterName }),
    );

    const items = document.createElement('div');
    items.className = 'shop-modal__items';
    for (const entry of offer?.items ?? []) {
      const template = getItem(entry.templateId);
      const slot = document.createElement('div');
      slot.className = 'slot';
      slot.title = uiText(template?.name ?? entry.templateId);
      const icon = document.createElement('div');
      icon.className = `item item--${template?.rarity ?? 'common'}`;
      paintItemIcon(icon, template, options.icons, entry.templateId);
      slot.append(icon);
      items.append(slot);
    }
    const hint = document.createElement('p');
    hint.className = 'shop-modal__hint';
    hint.style.textAlign = 'center';
    hint.textContent = uiText(
      'Équipe-les depuis l’onglet Apparence de ton inventaire : ils se portent par-dessus ton équipement.',
    );
    if (offer?.items.length === 1) {
      const template = getItem(offer.items[0]?.templateId ?? '');
      if (template !== undefined) name.style.color = hex(RARITY_COLORS[template.rarity]);
    }

    const actions = document.createElement('div');
    actions.className = 'shop-modal__actions';
    const show = document.createElement('button');
    show.type = 'button';
    show.className = 'shop-modal__button';
    show.textContent = uiText('Voir mon inventaire');
    show.addEventListener('click', () => {
      this.close();
      options.onShowInventory();
    });
    const done = document.createElement('button');
    done.type = 'button';
    done.className = 'shop-modal__button';
    done.textContent = uiText('Continuer à jouer');
    done.addEventListener('click', () => {
      this.close();
    });
    actions.append(show, done);

    frame.append(
      thanks,
      lead,
      items,
      hint,
      actions,
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );
    this.root.append(frame);
    // A click on the veil closes it, like Escape.
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown, true);
    show.focus();
  }

  close(): void {
    window.removeEventListener('keydown', this.onKeyDown, true);
    this.root.remove();
  }
}
